import { CommonUtility } from '@utility/common';
import { useMemo } from 'react';
import { CommonConstant } from '@utility/constants/common';
import { Info } from '@phosphor-icons/react';
import { cn, Tooltip } from '@skupreme/shared/ui';
import { MuteText } from '@elements/Common';
import { metadataHook } from '@hooks/metadata';
import { MarketplacePriority } from '@utility/constants/catalog';
import { getMarketplaceIcon } from './common';

const PARENT_IMAGE = 'sk:block sk:size-[35px] sk:object-scale-down';

export function ParentImages({ images, onClick }) {
  if (!CommonUtility.isValidArray(images)) {
    return (
      // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: the variations toggle beside it is the keyboard route
      <img
        alt=""
        className={cn(PARENT_IMAGE, 'sk:mx-auto')}
        onClick={onClick}
        src={CommonConstant.defaultImage}
      />
    );
  }

  /* Up to three thumbnails fanned out from the centre of the parent's box. */
  return images
    .filter((_, index) => index < 3)
    .map((item, index) => (
      // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: the variations toggle beside it is the keyboard route
      <img
        alt=""
        onClick={onClick}
        key={index}
        src={typeof item === 'string' ? item : undefined}
        className={cn(
          PARENT_IMAGE,
          'sk:absolute sk:cursor-pointer sk:border sk:border-border sk:bg-surface',
        )}
        style={{
          left: `calc(50% - 20px + ${index * 8}px)`,
          top: `calc(50% - 20px + ${index * 8}px)`,
        }}
      />
    ));
}

function SingleMarketplacePrice({
  icon,
  type,
  price,
  currency,
  base,
  priceRule,
}) {
  const currencySymbol = CommonUtility.currencySymbol(currency);
  const diff = (base - price).toFixed(2);
  const diffSymbol = price - base < 0 ? '-' : '+';
  // @ts-expect-error - TS2345: Argument of type 'string' is not assignable to parameter of type 'number'.
  const diffPercentage = ((100 * Math.abs(diff)) / base).toFixed(2);

  return (
    <div className="sk:grid sk:grid-cols-2 sk:items-center sk:gap-x-4 sk:py-2.5 sk:first:pt-0 sk:last:pb-0">
      <div className="sk:flex sk:items-center sk:gap-1 sk:whitespace-nowrap">
        {icon && (
          <img
            src={icon}
            alt={type}
            className="sk:size-6 sk:rounded-option sk:bg-fill-tertiary sk:object-contain sk:p-0.5"
          />
        )}
        {!icon && currencySymbol && (
          <div className="sk:flex sk:size-6 sk:items-center sk:justify-center sk:rounded-option sk:bg-fill-tertiary sk:text-fg-secondary">
            {currencySymbol}
          </div>
        )}
        <span>{type}</span>
      </div>
      <div className="sk:flex sk:items-center sk:justify-between sk:whitespace-nowrap">
        <span className="sk:font-semibold">
          {CommonUtility.currencyFormat(price, currency)}
        </span>
        {base ? (
          <span className="sk:ml-2 sk:text-xs sk:text-fg-tertiary">
            {priceRule === 'FIXED' ? (
              <>
                ({diffSymbol} {currencySymbol}
                {diff})
              </>
            ) : (
              <>
                {/* @ts-expect-error - ts-migration */}({diffSymbol}{' '}
                {!isNaN(diffPercentage) ? diffPercentage : 0}%)
              </>
            )}
          </span>
        ) : (
          <span className="sk:ml-2 sk:text-xs">&nbsp;</span>
        )}
      </div>
    </div>
  );
}

export function MarketPlacePrices({
  data,
  currency,
  regionCurrency,
  showLabel = true,
}) {
  const { marketplaces, is_parent, p_price, p_currency, price_rules } = data;
  const showCurrency = currency || p_currency || regionCurrency;
  const { metadata } = metadataHook();

  const sortedData = useMemo(() => {
    const temp = Object.keys(marketplaces || {})
      .map(key => ({
        key,
        ...marketplaces[key],
        icon: getMarketplaceIcon(metadata, key),
        priority: MarketplacePriority[key] || Date.now(),
      }))
      .sort((a, b) => a.priority - b.priority);
    return temp;
  }, [marketplaces, metadata]);

  if (is_parent) {
    return null;
  }
  const basePrice = CommonUtility.currencyFormat(
    p_price,
    Intl.supportedValuesOf('currency').includes(showCurrency)
      ? showCurrency
      : 'USD',
  );

  if (!CommonUtility.isValidObject(marketplaces)) {
    return basePrice;
  }

  return (
    <Tooltip
      placement="top"
      content={
        <div className="sk:max-h-[220px] sk:min-w-[280px] sk:divide-y sk:divide-rule sk:overflow-y-auto">
          {/* @ts-expect-error - TS2739: Type '{ currency: any; key: string; price: any; type: string... */}
          <SingleMarketplacePrice
            currency={showCurrency}
            key="Base Price"
            price={p_price}
            type="Base Price"
          />
          {sortedData.map(item => (
            <SingleMarketplacePrice
              base={p_price}
              currency={showCurrency}
              icon={item.icon}
              key={item.key}
              price={item.price}
              type={item.type}
              priceRule={price_rules}
            />
          ))}
        </div>
      }
    >
      <div className="sk:flex sk:cursor-pointer sk:flex-nowrap sk:items-center sk:justify-center sk:gap-1 sk:whitespace-nowrap">
        {showLabel && <MuteText className="sk:shrink-0">Base</MuteText>}
        <span className="sk:mx-1 sk:min-w-0 sk:shrink-0 sk:overflow-hidden">
          {basePrice}
        </span>
        <Info
          size={16}
          aria-hidden
          className="sk:ml-1 sk:shrink-0 sk:text-fg-tertiary"
        />
      </div>
    </Tooltip>
  );
}
